import { Checkbox, DatePicker, Input, Select, Tag, Tooltip } from 'antd';
import { MouseEvent } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import Icon from '../../components/Icon/Icon';
import RegistrationHeader from '../../components/Header/RegistrationHeader';
import Stepper, { Step } from '../../components/Stepper/Stepper';

import useRegistration from './useRegistration';
import { Role } from '../../Helpers/AuthorizationDataProvider';
import { mentoringScopes, positions, researchFields } from '../../Helpers/StaticData';

import styles from './Registration.module.scss';
import {WORDPRESS_DOMAIN} from "../../Helpers/EnvVars";
import {Meeting} from "./Registration.model";

const Registration = () => {
	const {
		state,
		disabledDate,
		formatDate,
		navigateTo,
		onPasswordChange,
		onSubmitGeneralInfoForm,
		onSubmitMentorForm,
		setFutureMeeting,
		validateEmailAddress,
	} = useRegistration();
	const {
		activeStep,
		futureMeetings,
		i18nCountries,
		i18nLanguages,
		futureMeeting,
		passwordStrength,
		previousMeetings,
		userStepperFinished,
		setActiveStep,
	} = state;

	const { Option } = Select;
	const CheckboxGroup = Checkbox.Group;

	const defaultValues: any = {
    acceptedCoc: false,
    acceptedTac: false,
    currentLocation: undefined,
    currentPosition: undefined,
    email: '',
    firstName: '',
    includeInNetwork: false,
    includeInSearch: false,
    institution: undefined,
    languages: [],
    lastName: '',
    location: undefined,
    mentoringScopes: [],
    motivation: undefined,
    password: '',
    passwordConfirm: '',
    previousLnlm: undefined,
    researchAreas: [],
    role: 'Mentee',
    startDate: null
  };

	const {
		clearErrors,
		control,
		getValues,
		handleSubmit,
		setValue,
		trigger,
		watch,
		formState: { errors }
	} = useForm({ defaultValues });
	
	const watchFirstName = watch("firstName");
	const watchLnlm = watch("previousLnlm");
	const watchPassword = watch("password");
	const watchRole = watch("role");

	const intl = useIntl();
	const { TextArea } = Input;

	const onClickNoMeeting = () => {
		setValue('previousLnlm', undefined);
		setFutureMeeting(undefined)
	};
	
	const onClickFutureMeeting = (value: string) => {
		setValue('previousLnlm', undefined);
		setFutureMeeting(value);
	};

	const onChangeMeeting = (value: string) => {
		setValue('previousLnlm', value);
		setFutureMeeting(undefined);
	}

	const onClickRole = (event: MouseEvent) => {
		const el = event.currentTarget;
		const role = el.getAttribute('data-role') as Role || 'Mentee';
		setValue('role', role);
		setActiveStep((currentValue) => currentValue + 1);
	}

	const handleStepBack = () => {
		clearErrors();
		setActiveStep((currentValue) => currentValue - 1);
	}

	const handleStepSubmit = async () => {
		let validEmail: boolean = true;

		switch (activeStep) {
			case 1:
				await trigger(['firstName', 'lastName']);
				break;
			case 2:
				await trigger(['email']);
				validEmail = await validateEmailAddress(getValues('email'));
				break;
			case 3:
				await trigger(['currentLocation']);
				break;
		}

		if (Object.keys(errors).length || !validEmail) {
			return;
		} else {
			setActiveStep((currentValue) => currentValue + 1)
		}
	}

	const handleMentorStepSubmit = async () => {
		switch (activeStep) {
			case 1:
				await trigger(['currentPosition', 'institution', 'researchAreas', 'location', 'startDate']);
				break;
			case 2:
				await trigger(['languages']);
				break;
			case 3:
				await trigger(['mentoringScopes']);
				break;
			case 4:
				await trigger(['motivation']);
				break;
		}

		if (Object.keys(errors).length) {
			return;
		} else {
			setActiveStep((currentValue) => currentValue + 1)
		}
	}

	const renderStep1 = () => (
		<div className={styles.fieldsContainer}>
			<div className={`${styles.registrationRadioButton} ${styles.fullWidth}`}>

				<div className={styles.option} data-role="Mentor" onClick={onClickRole}>
					<div>
						<Icon name="chalkboard-teacher"/>
						<label><FormattedMessage id="registration.step1.option1"/></label>
					</div>

					<Icon name="chevron-right" color="light"/>
				</div>

				<div className={styles.option} data-role="Mentee" onClick={onClickRole}>
					<div>
						<Icon name="user-graduate"/>
						<label><FormattedMessage id="registration.step1.option2"/></label>
					</div>

					<Icon name="chevron-right" color="light"/>
				</div>
			</div>
		</div>

	);

	const renderStep2 = () => (
		<div className={styles.fieldsContainer}>	
			<div className={`${styles.registrationInput} ${styles.halfWidth} ${errors.firstName ? styles.error : ''}`}>
				<label><FormattedMessage id="formLabels.firstName" />*</label>
				<Controller
					name="firstName"
					control={control}
					rules={{ required: intl.formatMessage({ id: "formErrors.firstName.required" }) }}
					render={({ field }) => (
						<Input
							{...field}
							bordered={false}
							className={errors.firstName && 'error'}
						/>
					)}
				/>
				{errors.firstName && <div className={styles.errorMessage}>{errors.firstName?.message}</div>}
			</div>

			<div className={`${styles.registrationInput} ${styles.halfWidth} ${errors.lastName ? styles.error : ''}`}>
				<label><FormattedMessage id="formLabels.lastName" />*</label>
				<Controller
					name="lastName"
					control={control}
					rules={{ required: intl.formatMessage({ id: "formErrors.lastName.required" }) }}
					render={({ field }) => (
						<Input
							{...field}
							bordered={false}
							className={errors.lastName && 'error'}
						/>
					)}
				/>
				{errors.lastName && <div className={styles.errorMessage}>{errors.lastName?.message}</div>}
			</div>
		</div>
	);

	const renderStep3 = () => (
		<div className={styles.fieldsContainer}>

			<div className={`${styles.registrationInput} ${errors.email ? styles.error : ''}`} key="emailField">
				<label><FormattedMessage id="formLabels.email" />*</label>
				<Controller
					name="email"
					control={control}
					rules={{
						pattern: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
						required: intl.formatMessage({ id: 'formErrors.email.required' }),
					}}
					render={({ field }) => (
						<Input
							{...field}
							bordered={false}
							className={errors.email && 'error'}
							type="email"
						/>
					)}
				/>
				{errors.email?.type === "required" && <div className={styles.errorMessage}>{errors.email?.message}</div>}
				{errors.email?.type === "pattern" && <div className={styles.errorMessage}><FormattedMessage id="formErrors.email.pattern"/></div>}
			</div>
		</div>
	);

	const renderStep4 = () => (
		<div className={styles.fieldsContainer}>

			<div className={`${styles.registrationSelect} ${styles.fullWidth} ${errors.currentLocation ? styles.error : ''}`}>
				<div>
					<label><FormattedMessage id="formLabels.location" /></label>
					<Controller
						name="currentLocation"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								bordered={false}
								filterOption={(input, option: any) =>
									option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
								optionFilterProp="children"
								placeholder={intl.formatMessage({id: 'formPlaceholders.location'})}
								showArrow={false}
								showSearch
							>
								{Object.keys(i18nCountries).map(code => (
									<Option key={code} value={code}>{i18nCountries[code]}</Option>
								))}
							</Select>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>
		</div>
	);

	const renderStep5 = () => (
		<div className={styles.fieldsContainer}>
			<div className={`${styles.registrationRadioButton} ${styles.fullWidth}`}>
				<div className={styles.option}>
					<div className={styles.selector}>
						<div
							className={`${styles.circle} ${
								watchLnlm && !futureMeeting ? styles.active : ''
							}`}
						></div>
						<label className={styles.bold}>
							<FormattedMessage id="basics.yes" />
							,&nbsp;
						</label>
						<Controller
							name="previousLnlm"
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									bordered={false}
									className={`${styles.dropdown} ${styles.lnlm}`}
									filterOption={(input, option: any) =>
										option.children
											.toLowerCase()
											.indexOf(input.toLowerCase()) >= 0
									}
									optionFilterProp="children"
									placeholder={intl.formatMessage({
										id: 'formPlaceholders.selectMeeting',
									})}
									showArrow={false}
									showSearch
									onChange={onChangeMeeting}
								>
									{previousMeetings.map((meeting: Meeting, index: number) => (
										<Option key={`${meeting}${index}`} value={meeting.id} className={styles.rowsOption}>
											<span>{meeting.name}</span> {formatDate(meeting.date)}
										</Option>
									))}
								</Select>
							)}
						></Controller>
					</div>

					<Icon name="chevron-down" color="light" />
				</div>
				{watchRole !== "Mentor" &&
					<div className={styles.option}>
						<div className={styles.selector}>
							<div
								className={`${styles.circle} ${
									futureMeeting ? styles.active : ''
								}`}
							></div>
							<label className={styles.bold}>
								<FormattedMessage id="basics.no" />
								,&nbsp;
							</label>
							<Select
								value={futureMeeting}
								bordered={false}
								className={`${styles.dropdown} ${styles.lnlm}`}
								filterOption={(input, option: any) =>
									option.children.toLowerCase().indexOf(input.toLowerCase()) >=
									0
								}
								optionFilterProp="children"
								placeholder={intl.formatMessage({
									id: 'formPlaceholders.futureMeeting',
								})}
								showArrow={false}
								showSearch
								onChange={onClickFutureMeeting}
							>
								{futureMeetings.map((item: Meeting, index: number) => (
									<Option key={`${item.id}${index}`} value={item.id} className={styles.rowsOption}>
										<span>{item.name}</span> {formatDate(item.date)}
									</Option>
								))}
							</Select>
						</div>
						<Icon name="chevron-down" color="light" />
					</div>
				}
				<div className={styles.option} onClick={onClickNoMeeting}>
					<div>
						<div
							className={`${styles.circle} ${
								!watchLnlm && !futureMeeting ? styles.active : ''
							}`}
						></div>
						<label>
							<span className={styles.bold}>
								<FormattedMessage id="basics.no" />
							</span>
							, &nbsp;
							<FormattedMessage id="registration.step5.option2" />
						</label>
					</div>
				</div>
			</div>
		</div>
	);

	const renderStep6 = () => (
		<div className={styles.fieldsContainer}>
			
			<div className={`${styles.registrationInput} ${styles.fullWidth} ${errors.password ? styles.error : ''}` }>
				<label><FormattedMessage id="formLabels.password" /></label>
				<Controller
					name="password"
					control={control}
					rules={{
						min: 8,
						pattern: /^(?=.*[a-z])(?=.*[0-9]).{8,}$/,
						required: intl.formatMessage({ id: 'formErrors.password.required' }),
					}}
					render={({ field }) => (
						<div className={styles.passwordWrapper}>
							<Input.Password
								{...field}
								bordered={false}
								className={errors.password && 'error'}
								visibilityToggle={false}
								onChange={(event) => {
									field.onChange(event);
									onPasswordChange(event);
								}}
							/>
							<span className={styles[passwordStrength]}><FormattedMessage id={`formErrors.password.${passwordStrength}`}/></span>
						</div>
					)}
				/>
				{errors.password?.type === "required" && <div className={styles.errorMessage}>{errors.password.message}</div>}
				{errors.password?.type === "pattern" && <div className={styles.errorMessage}><FormattedMessage id="formErrors.password.pattern"/></div>}
			</div>

			<div className={`${styles.registrationInput} ${styles.fullWidth} ${errors.passwordConfirm ? styles.error : ''}`}>
				<label><FormattedMessage id="formLabels.passwordRetype" /></label>
				<Controller
					name="passwordConfirm"
					control={control}
					rules={{
						required: intl.formatMessage({ id: 'formErrors.password.required' }),
						validate: value => value === watchPassword || intl.formatMessage({ id: 'formErrors.password.mismatch' })
					}}
					render={({ field }) => (
						<Input.Password
							{...field}
							className={errors.passwordConfirm && 'error'}
							bordered={false}
							visibilityToggle={false}
						/>
					)}
				/>
				{errors.passwordConfirm && <div className={styles.errorMessage}>{errors.passwordConfirm.message}</div>}
			</div>
			<div className={`${styles.checkbox} ${errors.acceptedTac ? styles.error : ''}`}>
				<Controller
					name="acceptedTac"
					control={control}
					rules={{
						required: true,
					}}
					render={({ field }) => (
						<Checkbox
							{...field}
							checked={field.value}
						>
							<FormattedMessage id="formLabels.consent"/>&nbsp;
							<a href={`${WORDPRESS_DOMAIN}/terms-and-conditions`} target="_blank" rel="noreferrer"><FormattedMessage id="formLabels.terms"/></a>
						</Checkbox>
					)}
				/>
			</div>

			<div className={`${styles.checkbox} ${errors.includeInNetwork ? styles.error : ''}`}>
				<Controller
					name="includeInNetwork"
					control={control}
					rules={{
						required: true,
					}}
					render={({ field }) => (
						<Checkbox
							{...field}
							checked={field.value}
						>
							<FormattedMessage id="formLabels.iAgreeToBeShown"/>
							<a href={`${WORDPRESS_DOMAIN}/faq`} target="_blank" rel="noreferrer"><FormattedMessage id="formLabels.otherUsersNetwork"/></a>
							<Tooltip title="The network section displays the mentors and mentees in a user’s network.">
								<span className={styles.tooltip}><Icon name="info" color="light" width="15" height="15" /></span>
							</Tooltip>
						</Checkbox>
					)}
				/>
			</div>

			<div className={`${styles.checkbox} ${errors.includeInSearch ? styles.error : ''}`}>
				<Controller
					name="includeInSearch"
					control={control}
					rules={{
						required: true,
					}}
					render={({ field }) => (
						<Checkbox
							{...field}
							checked={field.value}
						>
							<FormattedMessage id="formLabels.consentIncludeInSearch"/>
						</Checkbox>
					)}
				/>
			</div>
		</div>
	);

	const renderStep6Interrupted = () => (
		<div className={styles.fieldsContainer}>
			<p><FormattedMessage id="registration.step6Interrupted.description" values={{ br: <br /> }}/></p>
		</div>
	);

	const renderMentorStep1 = () => (
		<div className={styles.fieldsContainer}>
			<div className={`${styles.registrationRadioButton} ${styles.fullWidth}`}>
				<div className={styles.option} onClick={handleMentorStepSubmit}>
					<div>
						<label><FormattedMessage id="mentorRegistration.step1.option2"/></label>
					</div>
					<Icon name="chevron-right" color="light"/>
				</div>
			</div>
		</div>

	);

	const renderMentorStep2 = () => (
		<div className={`${styles.fieldsContainer}`}>
			<div className={`${styles.registrationSelect} ${styles.fullWidth} ${errors.currentPosition ? styles.error : ''}`} key="currentPositionField">
				<div>
					<label><FormattedMessage id="formLabels.position" /></label>
					<Controller
						name="currentPosition"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								bordered={false}
								placeholder={intl.formatMessage({id: 'formPlaceholders.position'})}
								showArrow={false}
							>
								{positions.map(field => (
									<Option key={field} value={field}><FormattedMessage id={`currentPosition.${field}`} /></Option>
								))}
							</Select>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>

			<div className={`${styles.registrationSelect} ${errors.institution ? styles.error : ''}`} key="institutionField">
				<div>
				<label><FormattedMessage id="formLabels.institution" /></label>
				<Controller
					name="institution"
					control={control}
					rules={{ required: true }}
					render={({ field }) => (
						<Input
							{...field}
							bordered={false}
							className={errors.institution && 'error'}
						/>
					)}
				/>
				</div>
			</div>
			
			<div className={`${styles.registrationSelect} ${styles.fullWidth} ${errors.researchAreas ? styles.error : ''}`} key="researchAreasField">
				<div>
					<label><FormattedMessage id="formLabels.researchAreas" /></label>
					<Controller
						name="researchAreas"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								bordered={false}
								mode="multiple"
								placeholder={intl.formatMessage({id: 'formPlaceholders.researchAreas'})}
								tagRender={({ label }) => (
									<Tag
										style={{
											border: 'none',
											textTransform: 'uppercase'
										}}
									>
										{label}
									</Tag>
								)}
							>
								{researchFields.map(field => (
									<Option key={field} value={field}><FormattedMessage id={`researchAreas.${field}`} /></Option>
								))}
							</Select>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>

			<div className={`${styles.registrationSelect} ${errors.location ? styles.error : ''}`} key="locationField">
				<div>
					<label><FormattedMessage id="formLabels.location" /></label>
					<Controller
						name="location"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								bordered={false}
								filterOption={(input, option: any) =>
									option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
								}
								optionFilterProp="children"
								placeholder={intl.formatMessage({id: 'formPlaceholders.location'})}
								showArrow={false}
								showSearch
							>
								{Object.keys(i18nCountries).map(code => (
									<Option key={code} value={code}>{i18nCountries[code]}</Option>
								))}
							</Select>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>

			<div className={`${styles.registrationSelect} ${errors.startDate ? styles.error : ''}`} key="startDateField">
				<div>
					<label><FormattedMessage id="formLabels.startDate" /></label>
					<Controller
						name="startDate"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<DatePicker
								{...field}
								bordered={false}
								disabledDate={disabledDate}
								format="DD MMM YYYY"
								suffixIcon={false}
								style={{ padding: 0 }}
							/>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>
		</div>

	);

	const renderMentorStep3 = () => (
		<div className={styles.fieldsContainer}>
			<div className={`${styles.registrationSelect} ${styles.fullWidth} ${errors.languages ? styles.error : ''}`} key="languagesField">
				<div>
					<label><FormattedMessage id="formLabels.languages" /></label>
					<Controller
						name="languages"
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								bordered={false}
								mode="multiple"
								optionFilterProp="children"
								placeholder={intl.formatMessage({id: 'formPlaceholders.language'})}
								tagRender={({ label }) => (
									<Tag
										style={{
											border: 'none',
											textTransform: 'uppercase'
										}}
									>
										{label}
									</Tag>
								)}
							>
								{Object.keys(i18nLanguages).map(code => (
									<Option key={code} value={code}>{i18nLanguages[code]}</Option>
								))}
							</Select>
						)}
					/>
				</div>

				<Icon name="chevron-down" color="light"/>
			</div>
		</div>
	);

	const renderMentorStep4 = () => (
		<div className={`${styles.mentoringScopesFieldsContainer} ${styles.fullWidth}`}>
			<Controller
				name="mentoringScopes"
				control={control}
				rules={{
					required: true,
				}}
				render={({ field }) => (
					<CheckboxGroup {...field} style={{ width: '100%' }} className="registrationCheckbox">
					<div className={`${styles.registrationCheckbox} ${errors.mentoringScopes ? styles.error : ''}`}>
						{ mentoringScopes.map((disposition: string) => (
							<div className={styles.option} key={disposition}>
								<Checkbox value={disposition}><FormattedMessage id={`mentoringScopes.${disposition}`}/></Checkbox>
							</div>
						))}
					</div>
				</CheckboxGroup>
				)}
			/>
		</div>
	);

	const renderMentorStep5 = () => (
		<div className={styles.fieldsContainer}>

			<div className={`${styles.registrationInput} ${styles.fullWidth} ${errors.motivation ? styles.error : ''}`} key="motivationField">
				<label><FormattedMessage id="formLabels.message" /></label>
				<Controller
					name="motivation"
					control={control}
					rules={{ required: true }}
					render={({ field }) => (
						<TextArea
							{...field}
							autoSize={{maxRows: 8, minRows: 8, }}
							bordered={false}
							className={errors.motivation && 'error'}
							placeholder={intl.formatMessage({id: 'formPlaceholders.motivation'})}
						/>
					)}
				/>
			</div>
			<div className={`${styles.checkbox} ${errors.acceptedCoc ? styles.error : ''}`}>
				<Controller
					name="acceptedCoc"
					control={control}
					rules={{
						required: true,
					}}
					render={({ field }) => (
						<Checkbox
							{...field}
							checked={field.value}
						>
							<FormattedMessage id="formLabels.consent"/>&nbsp;
							<a href={`${WORDPRESS_DOMAIN}/code-of-conduct`} target="_blank" rel="noreferrer"><FormattedMessage id="formLabels.codeOfConduct"/></a>.
						</Checkbox>
					)}
				/>
			</div>
		</div>
	);

	return (
		<div className={styles.pageWrapper}>
			<div className={styles.pageContainer}>

				<div className={styles.pageContent}>

					<RegistrationHeader
						actionLabel={intl.formatMessage({ id: 'buttons.login' })}
						onAction={navigateTo.bind(null, '/login')}
					/>
					<div className={styles.content}>
						{!userStepperFinished &&
						<Stepper
							step={activeStep}
							title={intl.formatMessage({ id: 'registration.title'})}
							onFinish={handleSubmit(onSubmitGeneralInfoForm)}
							onStepBack={handleStepBack}
							onStepSubmit={handleStepSubmit}
							nextLabel={activeStep !== 5 ? intl.formatMessage({ id: 'buttons.next'}) : intl.formatMessage({ id: 'buttons.finish'})}
						>
							<Step title={intl.formatMessage({ id: 'registration.step1.subtitle'})} hiddenNavigation={true}>
								{ renderStep1() }
							</Step>
							<Step title={intl.formatMessage({ id: 'registration.step2.subtitle'})}>
								{ renderStep2() }
							</Step>
							<Step title={intl.formatMessage({ id: 'registration.step3.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b>, br: <br />, name: watchFirstName, } )}>
								{ renderStep3() } 
							</Step>
							<Step title={intl.formatMessage({ id: 'registration.step4.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b>, br: <br />, })}>
								{ renderStep4() }
							</Step>
							<Step title={intl.formatMessage({ id: 'registration.step5.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b>, br: <br />, })}>
								{ renderStep5() }
							</Step>
							{(watchLnlm || futureMeeting) ?
							<Step title={intl.formatMessage({ id: 'registration.step6.subtitle'}, { br: <br /> })}>
								{ renderStep6() }
							</Step> :
							<Step title={intl.formatMessage({ id: 'registration.step6Interrupted.subtitle'}, { br: <br /> })}>
							{ renderStep6Interrupted() }
						</Step>
							}
						</Stepper>
						}

						{userStepperFinished && watchRole !== 'Mentee' &&
						<Stepper
							step={activeStep}
							title={intl.formatMessage({ id: 'mentorRegistration.title'})}
							onFinish={handleSubmit(onSubmitMentorForm)}
							onStepBack={handleStepBack}
							onStepSubmit={handleMentorStepSubmit}
						>
							<Step title={intl.formatMessage({ id: 'mentorRegistration.step1.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b>, br: <br />, })} hiddenNavigation={true}>
								{renderMentorStep1()}
							</Step>
							<Step title={intl.formatMessage({ id: 'mentorRegistration.step2.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b> })}>
								{renderMentorStep2()}
							</Step>
							<Step title={intl.formatMessage({ id: 'mentorRegistration.step3.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b> })}>
								{renderMentorStep3()}
							</Step>
							<Step title={intl.formatMessage({ id: 'mentorRegistration.step4.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b> })}>
								{renderMentorStep4()}
							</Step>
							<Step title={intl.formatMessage({ id: 'mentorRegistration.step5.subtitle' }, { b: (...chunks) => <b><i>{chunks}</i></b> })}>
								{renderMentorStep5()}
							</Step>
						</Stepper>
						}

					</div>

				</div>
				
				<div className={styles.pageSider}></div>
			</div>

		</div>
	);
};

export default Registration;